import React, { createContext, useContext, ReactNode, useEffect } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';

interface GoogleMapsContextType {
  isLoaded: boolean;
  loadError: Error | undefined;
  isApiKeyMissing: boolean;
}

const GoogleMapsContext = createContext<GoogleMapsContextType>({
  isLoaded: false,
  loadError: undefined,
  isApiKeyMissing: false,
});

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const libraries: 'places'[] = ['places'];

export function GoogleMapsProvider({ children }: { children: ReactNode }) {
  const isApiKeyMissing = !GOOGLE_MAPS_API_KEY;
  
  // Only attempt to load Google Maps if API key is present
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY || '',
    libraries,
    preventGoogleFontsLoading: true,
    // Don't load if no API key to prevent unnecessary errors
    ...(!GOOGLE_MAPS_API_KEY && { googleMapsApiKey: undefined }),
  });

  // Log Google Maps status for debugging (but don't crash the app)
  useEffect(() => {
    if (isApiKeyMissing) {
      console.warn('Google Maps API key is missing. Map features will be disabled.');
    } else if (loadError) {
      console.error('Google Maps failed to load:', loadError.message);
      console.warn('App will continue to work, but map features will be disabled.');
    } else if (isLoaded) {
      console.log('Google Maps loaded successfully.');
    }
  }, [isLoaded, loadError, isApiKeyMissing]);

  // Always render children regardless of Google Maps status
  // This ensures the app remains accessible even if Maps fail
  return (
    <GoogleMapsContext.Provider value={{ isLoaded, loadError, isApiKeyMissing }}>
      {children}
    </GoogleMapsContext.Provider>
  );
}

export function useGoogleMaps() {
  const context = useContext(GoogleMapsContext);
  if (context === undefined) {
    throw new Error('useGoogleMaps must be used within a GoogleMapsProvider');
  }
  return context;
}

// Helper hook to safely check if Google Maps features should be enabled
export function useGoogleMapsEnabled() {
  const { isLoaded, loadError, isApiKeyMissing } = useGoogleMaps();
  return isLoaded && !loadError && !isApiKeyMissing;
} 